import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Youtube, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { pdfDownloadUrl } from "@/lib/pdfDownload";
import { NewsletterSignup } from "@/components/NewsletterSignup";

const Journal = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="The Journal | Rob Lasa"
        description="Written reflections and raw video entries. Not scripted. Not optimized. Just what's true."
        keywords="journal, reflections, nice guy, identity, men's mental health, honest content"
        url="/journal"
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-90" />
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-display font-bold text-primary-foreground mb-6">
              The Journal
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/90 max-w-2xl mx-auto">
              Written reflections and raw video entries. Not scripted. Not optimized. Just what's true.
            </p>
          </div>
        </div>
      </section>

      {/* YouTube */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Youtube className="w-8 h-8 text-[#FF0000]" />
              <h2 className="text-3xl font-display font-bold">YouTube</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-8">
              Long-form, unscripted, real. This is where the raw work happens.
            </p>

            <div className="mb-8">
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-bold">
                <iframe
                  src="https://www.youtube.com/embed/DHlgKeP3yvo"
                  title="Rob Lasa - Latest"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-[#FF0000] text-white hover:bg-[#CC0000] gap-2">
                <a href="https://www.youtube.com/@souldadrob" target="_blank" rel="noopener noreferrer">
                  <Youtube className="w-5 h-5" />
                  Subscribe on YouTube
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2 border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000] hover:text-white">
                <a href="https://www.youtube.com/@souldadrob" target="_blank" rel="noopener noreferrer">
                  View All Videos
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Written */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="w-8 h-8 text-accent" />
              <h2 className="text-3xl font-display font-bold">Written Reflections</h2>
            </div>
            <p className="text-lg text-muted-foreground mb-8">
              Longer thoughts. Things I'm sitting with. Written honestly.
            </p>

            <Card className="p-8">
              <h3 className="text-2xl font-display font-bold mb-3">Blog</h3>
              <p className="text-muted-foreground mb-6">
                Written reflections on identity, nice guy patterns, and the messy work of becoming honest.
              </p>
              <Button asChild variant="outline" className="gap-2">
                <Link to="/blog">
                  Read the Blog
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Something to Sit With */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-display font-bold mb-3">Something to Sit With</h2>
            <p className="text-lg text-muted-foreground mb-8">
              A couple of things I wrote. No homework. Just something to sit with.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-8 flex flex-col">
                <h3 className="text-xl font-display font-bold mb-2">Nice vs. Kind</h3>
                <p className="text-muted-foreground mb-6 flex-1">
                  There's a difference between being nice and being kind. One is a performance. The other costs something.
                </p>
                <Button asChild variant="outline" className="gap-2 w-fit">
                  <a href={pdfDownloadUrl("nice-vs-kind", "journal")} rel="noopener">
                    <ArrowRight className="w-4 h-4" />
                    Read It
                  </a>
                </Button>
              </Card>

              <Card className="p-8 flex flex-col">
                <h3 className="text-xl font-display font-bold mb-2">You Can't Give What You Don't Have</h3>
                <p className="text-muted-foreground mb-6 flex-1">
                  You can't pour from empty. A reflection on why taking care of yourself isn't selfish — it's necessary.
                </p>
                <Button asChild variant="outline" className="gap-2 w-fit">
                  <a href={pdfDownloadUrl("you-cant-give-what-you-dont-have", "journal")} rel="noopener">
                    <ArrowRight className="w-4 h-4" />
                    Read It
                  </a>
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Email CTA — replaces old Support section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10 space-y-5">
            <p className="text-sm uppercase tracking-widest text-foreground/60">
              If any of this landed
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground leading-tight">
              Don't lose the thread.
            </h2>
            <p className="text-lg text-foreground/80 leading-relaxed">
              I'm not going to fill your inbox. When something real comes up — a video, a piece I wrote at 5am, a thought I can't shake — I'll send it. That's it. No course. No funnel. No "5 ways to be a better man."
            </p>
            <p className="text-base text-foreground/70 italic">
              Just one guy, writing it down, in case it helps another guy hear it.
            </p>
          </div>
        </div>
        <NewsletterSignup source="journal-cta" />
      </section>

      <Footer />
    </div>
  );
};

export default Journal;
